import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService, { isMfaChallenge } from '../../services/auth.service';
import { nextPathForToken } from '../../lib/roleRouting';
import { colors, gradients } from '../../styles/theme';
import { challengeHandoff } from './signIn.helpers';
import { readLinkToken } from '../../lib/linkToken';

export default function OAuthCallback(): JSX.Element {
  const navigate = useNavigate();
  // Guard against React 18 StrictMode double-invocation in dev: the
  // exchange code is single-use server-side, so a second call would
  // fail with `invalid_or_expired`. Pinning the first run keeps the
  // error from masking a successful login.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    // The code rides in the fragment, which no access log sees (#329); an error, which is
    // no credential, in the query string.
    const code = readLinkToken('code');
    const error = new URLSearchParams(globalThis.location.search).get('error');

    if (error) {
      navigate(`/login?error=${encodeURIComponent(error)}`, { replace: true });
      return;
    }

    if (!code) {
      navigate('/login?error=oauth_failed', { replace: true });
      return;
    }

    // Strip the code from the URL ASAP so a refresh/back-button or a
    // child request's Referer doesn't carry it. The token swap below
    // is what actually authenticates; the URL string is just a handoff.
    globalThis.history.replaceState({}, document.title, '/oauth/callback');

    authService.exchangeOAuthCode(code).then((tokenData) => {
      if (!tokenData) {
        navigate('/login?error=oauth_failed', { replace: true });
        return;
      }
      // The account still owes its own second factor: there is no session
      // yet. Hand the challenge to the login page's second-factor step in
      // navigation state — its `mfa_token` is a single-use credential, so
      // it never goes in the URL, in browser storage or in the auth store.
      if (isMfaChallenge(tokenData)) {
        navigate('/login', { replace: true, state: challengeHandoff(tokenData) });
        return;
      }
      // `exchangeOAuthCode` persisted the session through the auth
      // store, with the account's address read from the token, so the
      // topbar / dashboard show the right account while /auth/me runs.
      // The exchange response doesn't carry first_name / last_name.
      authService.hydrateSignedInUser();
      navigate(nextPathForToken(tokenData), { replace: true });
    });
  }, [navigate]);

  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: colors.navy }}
    >
      <div className="text-center">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg mx-auto mb-6"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          Q
        </div>
        <p className="text-white/60 text-sm">Processing authentication...</p>
      </div>
    </div>
  );
}
